import { isAxiosError } from "axios";
import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import type { QueryClient } from "react-query";
import * as z from "zod/mini";
import { describeRequest } from "@/lib/interactionTrace";
import { normalizeError } from "@/lib/normalizeError";

/**
 * Forwards uncaught errors and unhandled rejections to Sentry with their
 * source location. Idempotent: assigning the handlers again is a no-op.
 * Runs at module scope on the client for both the Pages Router (`_app.tsx`)
 * and the App Router shell (`AppProviders`).
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;

  window.onerror = (message, source, lineno, colno, error) => {
    Sentry.captureException(error, {
      extra: { message, source, lineno, colno },
    });
  };

  window.onunhandledrejection = (event) => {
    Sentry.captureException(event.reason, {
      extra: { type: "unhandledrejection", reason: event.reason },
    });
  };
}

const CachedUser = z.object({
  id: z.number(),
  name: z.string().check(z.minLength(1)),
});

const CachedConnectedOrgs = z.object({
  active_org_name: z.nullish(z.string().check(z.minLength(1))),
});

/**
 * The failed request of an Axios error as method and route template, e.g.
 * `GET /api/assistant/sessions/{id}/messages`; null fields for other errors.
 * Never the URL's query string, headers or bodies.
 */
function failedRequest(error: unknown): {
  request_method: string | null;
  request_route: string | null;
} {
  const config = isAxiosError(error) ? error.config : undefined;
  if (!config) return { request_method: null, request_route: null };

  return {
    request_method: config.method?.toUpperCase() ?? null,
    request_route: describeRequest(config).route,
  };
}

/**
 * What the PostHog `error` event records of a thrown value: its name,
 * message, code, HTTP status, stack and failed request, never the raw error,
 * whose Axios config and response would carry URLs, bodies and headers.
 */
export function errorAnalyticsProperties(error: unknown) {
  const normalized = normalizeError(error);
  const objectThrow =
    typeof error === "object" && error !== null && !(error instanceof Error);
  const message = objectThrow
    ? (ThrownMessage.safeParse(error).data?.message.slice(0, 500) ?? null)
    : normalized.error_message;

  return { ...normalized, error_message: message, ...failedRequest(error) };
}

const ThrownMessage = z.object({ message: z.string() });

/** The cached `connectedGithubOrgs` entry's active org, or null when absent or malformed. */
export function cachedActiveGithubOrg(queryClient: QueryClient): string | null {
  const cachedOrgs = queryClient.getQueryData("connectedGithubOrgs");

  return (
    CachedConnectedOrgs.safeParse(cachedOrgs).data?.active_org_name ?? null
  );
}

/**
 * Reports an error that reached the top-level boundary to Sentry and
 * PostHog with the signed-in user's id, active org, and HTTP failure details
 * from the query cache, so incidents can be split by user, org, and status
 * class.
 */
export function reportBoundaryError(error: Error, queryClient: QueryClient) {
  const activeGithubOrg = cachedActiveGithubOrg(queryClient);
  const user = CachedUser.safeParse(queryClient.getQueryData("user")).data;
  const response = isAxiosError(error) ? error.response : undefined;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({ id: user.id, username: user.name });
    }

    if (activeGithubOrg) {
      scope.setTag("organization", activeGithubOrg);
      scope.setExtra("activeGithubOrg", activeGithubOrg);
    }

    if (response) {
      scope.setExtra("error_response", response.data);
      scope.setExtra("error_status", response.status);
    }
    const request = failedRequest(error);
    if (request.request_route) {
      scope.setExtra("error_request", request);
    }

    const status = response?.status;
    if (status && status >= 500) {
      scope.setLevel("error");
    } else if (status && status >= 400) {
      scope.setLevel("warning");
    }

    Sentry.captureException(error);
  });

  posthog.capture("error", {
    ...errorAnalyticsProperties(error),
    orgName: activeGithubOrg,
  });
}
